import React, { useState, useRef } from 'react'

import Button from '@material-ui/core/Button'
import grey from '@material-ui/core/colors/grey'
import Paper from '@material-ui/core/Paper'
import { withStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'

import classNames from 'classnames'

import { post } from '~/tools/request'
import { getPath } from '~/utils'

const styles = (theme) => ({
    loginPanel: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 48px)',
    },
    loginCard: {
        width: 500,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 300,
    },
    loginTitle: {
        borderBottom: '1px solid ' + grey[200],
        paddingLeft: 40,
        paddingTop: 20,
        paddingBottom: 12,
    },
    logo: {
        width: 120,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: 40,
        paddingRight: 40,
        flex: 1
    },
    fields: {
        width: '100%',
    },
    field: {
        marginBottom: 15,
        width: '100%',
    },
    email: {
        marginTop: 40,
    },
    errorMessage: {
        marginBottom: 15,
    },
    successMessage: {
        marginBottom: 15,
    },

    submitButton: {
        color: 'white',
        borderRadius: 25,
        width: '100%',
        height: 48,
    },

    resendButton: {
        alignSelf: 'flex-end',
    },
    dividerStyle: {
        display: 'flex',
        alignItems: 'center',
        margin: '24px 0',
        color: '#666',
        fontSize: '14px',
    },

    dividerLineStyle: {
        flex: 1,
        height: '1px',
        backgroundColor: '#e0e0e0',
    },

    dividerTextStyle: {
        padding: '0 16px',
    },

    googleButtonStyle: {
        width: '100%',
        padding: '12px 0',
        borderRadius: '25px',
        border: '1px solid #e0e0e0',
        backgroundColor: 'white',
        color: '#333',
        // fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'background-color 0.2s ease',
        marginBottom: 45,
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
})

const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24">
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
)

function Login(props) {
    const {
        classes,
        staticRoot,
        requestLoginLinkAPIURL,
        authGoogleURL,
    } = props

    const [state, setState] = useState({
        email: '',
        emailError: '',
        errorMessage: '',
        successMessage: '',
        loggingIn: false
    })

    const emailFieldRef = useRef(null)

    const handleFieldChange = (field) => (event) => {
        setState((prev) => ({
            ...prev,
            [field]: event.target.value,
            // Clear inline errors / messages while user types
            emailError: field === 'email' ? '' : prev.emailError,
            errorMessage: '',
            successMessage: '',
        }))
    }

    const handleLoginWithLoginLink = (e) => {
        e.preventDefault()

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(state.email)) {
            setState((prev) => ({ ...prev, emailError: gettext('Please enter a valid email') }))
            emailFieldRef.current?.focus()
            return
        }

        setState((prev) => ({ ...prev, loggingIn: true, errorMessage: '', successMessage: '' }))

        post(requestLoginLinkAPIURL, {
            email: state.email,
        })
            .then((response) => {
                if (response.ok) {
                    setState((prev) => ({
                        ...prev,
                        successMessage: gettext(
                            'A login link has been sent to your email. Please check your inbox to continue.'
                        ),
                        errorMessage: '',
                    }))
                } else {
                    setState((prev) => ({ ...prev, errorMessage: response.error || gettext('Something went wrong, please try again.') }))
                }
            })
            .catch(() => {
                setState((prev) => ({ ...prev, errorMessage: gettext('Network error, please try again.') }))
            })
            .finally(() => {
                setState((prev) => ({ ...prev, loggingIn: false }))
            })
    }

    const handleLoginWithGoogle = () => {
        const params = new Proxy(new URLSearchParams(window.location.search), {
            get: (searchParams, prop) => searchParams.get(prop),
        })
        const next = params?.redirect_path || getPath('home')
        window.location.href = authGoogleURL + '?next=' + next
    }

    return (
        <form className={classes.loginPanel}>
            <Paper className={classes.loginCard}>
                <div className={classes.loginTitle}>
                    <img className={classes.logo} src={staticRoot + 'web/img/oakra-logo-black.png'} />
                </div>
                <div className={classes.container}>
                    {!state.successMessage && <div className={classes.fields}>
                        <TextField
                            className={classNames(classes.field, classes.email)}
                            variant="outlined"
                            label={gettext('Email address')}
                            value={state.email}
                            onChange={handleFieldChange('email')}
                            error={Boolean(state.emailError)}
                            helperText={state.emailError}
                            autoFocus
                            inputRef={emailFieldRef}
                        />

                        {state.errorMessage && (
                            <Typography className={classes.errorMessage} color="error" variant="body2">
                                {state.errorMessage}
                            </Typography>
                        )}

                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.submitButton}
                            disabled={state.loggingIn}
                            onClick={handleLoginWithLoginLink}
                        >
                            {gettext('Send link')}
                        </Button>

                        <div className={classes.dividerStyle}>
                            <div className={classes.dividerLineStyle}></div>
                            <span className={classes.dividerTextStyle}>OR</span>
                            <div className={classes.dividerLineStyle}></div>
                        </div>

                        <Button
                            className={classes.googleButtonStyle}
                            variant="contained"
                            onClick={handleLoginWithGoogle}
                        >
                            <GoogleIcon />
                            <span style={{ marginLeft: 10 }}>{gettext('Continue with Google')}</span>
                        </Button>                        
                    </div>}
                    {state.successMessage && (
                        <Typography className={classes.successMessage} variant="body2">
                            {state.successMessage}
                        </Typography>
                    )}
                </div>


            </Paper>
        </form>
    )
}

export default withStyles(styles)(Login)
